<script>
import ListHeader from '@/components/common/ListHeader.vue';
import FilterChips from '@/components/common/FilterChips.vue';
import ListPagination from '@/components/common/ListPagination.vue';
import CompaniesFilters from '@/components/companies/CompaniesFilters.vue';
import CompaniesTable from '@/components/companies/CompaniesTable.vue';
import DeleteCompanyModal from '@/components/companies/DeleteCompanyModal.vue';
import { companyService } from '@/shared/services/company.service';
import notificationService from '@/shared/services/notification.service';
import {
  countActiveFilters,
  fromCriteria,
  toCriteria,
} from '@/shared/utils/list-filters';

/**
 * L'etat de repos de chaque critere. Tout ce qui s'en ecarte filtre, compte
 * dans la pastille du panneau, porte une pastille sous la barre, et part dans
 * le prereglage qu'on enregistre.
 */
const EMPTY_FILTERS = {
  searchQuery: '',
  governorates: [],
  sectors: [],
  hasSurveys: '',
  hasEmail: '',
  isOffshore: '',
  yearFounded: { from: null, to: null },
  dateAdded: { from: null, to: null },
};

export default {
  name: 'CompaniesList',
  components: {
    ListHeader,
    FilterChips,
    ListPagination,
    CompaniesFilters,
    CompaniesTable,
    DeleteCompanyModal,
  },
  data() {
    return {
      companies: [],
      loading: false,
      error: null,
      exporting: false,
      filterTimeout: null,
      filters: { ...EMPTY_FILTERS },
      appliedFilters: { ...EMPTY_FILTERS },
      filterOptions: {
        governorates: [],
        sectors: [],
        yearFoundedMin: null,
        yearFoundedMax: null,
      },
      panelOpen: false,
      appliedFilterId: null,
      // Rappeler un prereglage applique ses criteres tout de suite; la
      // temporisation de saisie n'a pas a relancer la meme recherche 400 ms
      // plus tard.
      skipNextSchedule: false,
      currentPage: 1,
      perPage: 10,
      totalCompanies: 0,
      totalPages: 0,
      deleteModal: false,
      companyToDelete: null,
    };
  },
  computed: {
    activeCount() {
      return countActiveFilters(this.filters, EMPTY_FILTERS);
    },

    /** Ce qu'on enregistre sous un nom: les criteres qui filtrent, rien d'autre. */
    criteria() {
      return toCriteria(this.filters, EMPTY_FILTERS);
    },

    chips() {
      const out = [];
      const f = this.appliedFilters;

      if (f.searchQuery) {
        out.push({ key: 'searchQuery', label: `Recherche : ${f.searchQuery}` });
      }
      if (f.governorates?.length) {
        out.push({
          key: 'governorates',
          label: `Gouvernorats : ${f.governorates.join(', ')}`,
        });
      }
      if (f.sectors?.length) {
        out.push({
          key: 'sectors',
          label: `Secteurs : ${f.sectors.join(', ')}`,
        });
      }
      if (f.hasSurveys) {
        out.push({
          key: 'hasSurveys',
          label:
            f.hasSurveys === 'yes'
              ? 'Avec au moins une enquête'
              : 'Sans aucune enquête',
        });
      }
      if (f.hasEmail) {
        out.push({
          key: 'hasEmail',
          label:
            f.hasEmail === 'yes' ? 'Email renseigné' : 'Email non renseigné',
        });
      }
      if (f.isOffshore) {
        out.push({
          key: 'isOffshore',
          label: f.isOffshore === 'yes' ? 'Offshore' : 'Non offshore',
        });
      }
      if (f.yearFounded?.from || f.yearFounded?.to) {
        const from = f.yearFounded.from;
        const to = f.yearFounded.to;
        const label =
          from && to
            ? `${from} — ${to}`
            : from
              ? `depuis ${from}`
              : `jusqu'à ${to}`;

        out.push({ key: 'yearFounded', label: `Création : ${label}` });
      }
      if (f.dateAdded?.from || f.dateAdded?.to) {
        const from = f.dateAdded.from;
        const to = f.dateAdded.to;
        const label =
          from && to
            ? `du ${from} au ${to}`
            : from
              ? `depuis le ${from}`
              : `jusqu'au ${to}`;

        out.push({ key: 'dateAdded', label: `Ajout ${label}` });
      }

      return out;
    },

    hasFilters() {
      return this.chips.length > 0;
    },
  },
  mounted() {
    this.fetchCompanies();
    this.fetchFilterOptions();
  },
  beforeUnmount() {
    if (this.filterTimeout) {
      clearTimeout(this.filterTimeout);
    }
  },
  methods: {
    /** Les criteres actifs, traduits en parametres que l'API lit. */
    queryParams() {
      const f = this.appliedFilters;

      const params = {
        searchText: f.searchQuery || undefined,
        governorates: f.governorates?.length ? f.governorates : undefined,
        sectors: f.sectors?.length ? f.sectors : undefined,
        hasSurveys: f.hasSurveys ? f.hasSurveys === 'yes' : undefined,
        hasEmail: f.hasEmail ? f.hasEmail === 'yes' : undefined,
        isOffshore: f.isOffshore ? f.isOffshore === 'yes' : undefined,
        yearFoundedFrom: f.yearFounded?.from ?? undefined,
        yearFoundedTo: f.yearFounded?.to ?? undefined,
        dateAddedFrom: f.dateAdded?.from || undefined,
        dateAddedTo: f.dateAdded?.to || undefined,
      };

      Object.keys(params).forEach((key) => {
        if (params[key] === undefined) {
          delete params[key];
        }
      });

      return params;
    },

    /**
     * Ce que les choix multiples proposent vient de la base: une liste ecrite
     * en dur proposerait des gouvernorats qui ne rendent rien, et manquerait
     * ceux qu'une saisie vient d'introduire. Un echec laisse les listes vides
     * plutot que de bloquer l'ecran.
     */
    async fetchFilterOptions() {
      try {
        this.filterOptions = await companyService.getFilterOptions();
      } catch (err) {
        this.filterOptions = {
          governorates: [],
          sectors: [],
          yearFoundedMin: null,
          yearFoundedMax: null,
        };
      }
    },

    async fetchCompanies() {
      this.loading = true;
      this.error = null;

      try {
        const payload = await companyService.getCompanies({
          page: Number(this.currentPage) || 1,
          limit: Number(this.perPage) || 10,
          ...this.queryParams(),
        });

        if (payload?.data) {
          this.companies = Array.isArray(payload.data) ? payload.data : [];
          this.totalCompanies = payload.meta?.total ?? this.companies.length;
          this.totalPages =
            payload.meta?.totalPages ??
            Math.ceil(this.totalCompanies / this.perPage);
        } else if (Array.isArray(payload)) {
          this.companies = payload;
          this.totalCompanies = payload.length;
          this.totalPages = 1;
        } else {
          this.companies = payload?.items ?? [];
          this.totalCompanies = payload?.meta?.total ?? this.companies.length;
          this.totalPages =
            payload?.meta?.totalPages ??
            Math.ceil(this.totalCompanies / this.perPage);
        }
      } catch (err) {
        console.error(err);
        this.error =
          err.response?.data?.message || err.message || 'Erreur serveur';
        notificationService.toastError(this.error);
        this.companies = [];
        this.totalCompanies = 0;
        this.totalPages = 0;
      } finally {
        this.loading = false;
      }
    },

    // Filters apply on their own; the chips report what is active.
    scheduleFetch() {
      if (this.skipNextSchedule) {
        this.skipNextSchedule = false;
        return;
      }

      if (this.filterTimeout) {
        clearTimeout(this.filterTimeout);
      }

      // Une main sur les criteres s'ecarte du prereglage rappele: la liste
      // deroulante ne doit plus pretendre le montrer.
      this.appliedFilterId = null;

      this.filterTimeout = setTimeout(() => {
        this.applyNow();
      }, 400);
    },

    applyNow() {
      this.appliedFilters = { ...this.filters };
      this.currentPage = 1;
      this.fetchCompanies();
    },

    /** Rappelle un prereglage: ses criteres sont reposes, la liste se relance. */
    applySavedFilter(filter) {
      if (!filter) {
        return;
      }

      if (this.filterTimeout) {
        clearTimeout(this.filterTimeout);
      }

      this.skipNextSchedule = true;
      this.filters = fromCriteria(filter.criteria, EMPTY_FILTERS);
      this.appliedFilterId = filter.id;
      this.panelOpen = countActiveFilters(this.filters, EMPTY_FILTERS) > 0;
      this.applyNow();
    },

    /**
     * Le prereglage marque par defaut s'applique a l'ouverture, mais jamais
     * par-dessus des criteres deja regles a la main.
     */
    onSavedFiltersLoaded(defaultFilter) {
      if (defaultFilter && this.activeCount === 0) {
        this.applySavedFilter(defaultFilter);
      }
    },

    clearFilter(key) {
      this.skipNextSchedule = true;
      this.filters = { ...this.filters, [key]: EMPTY_FILTERS[key] };
      this.appliedFilterId = null;
      this.applyNow();
    },

    clearAllFilters() {
      this.skipNextSchedule = true;
      this.filters = { ...EMPTY_FILTERS };
      this.appliedFilterId = null;
      this.applyNow();
    },

    changePage(page) {
      this.currentPage = page;
      this.fetchCompanies();
    },

    viewCompany(id) {
      this.$router.push({ name: 'company-details', params: { id } });
    },

    /**
     * Demarrer une enquete depuis la liste: l'entreprise est deja connue, son
     * identite n'a pas a etre resaisie. Le formulaire la reprend a partir de
     * l'identifiant passe en parametre d'URL.
     */
    startSurveyFor(id) {
      this.$router.push({ name: 'Survey', query: { companyId: id } });
    },

    confirmDelete(company) {
      this.companyToDelete = company;
      this.deleteModal = true;
    },

    onDeleted(deletedId) {
      this.companies = this.companies.filter((c) => c.id !== deletedId);
      this.totalCompanies = Math.max(0, this.totalCompanies - 1);

      if (this.companies.length === 0 && this.currentPage > 1) {
        this.currentPage -= 1;
      }

      this.fetchCompanies();
    },

    async exportCompanies() {
      if (this.exporting) {
        return;
      }

      this.exporting = true;

      try {
        const blob = await companyService.exportCompanies({
          page: 1,
          limit: 10000,
          ...this.queryParams(),
        });

        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute(
          'download',
          `companies_${new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-')}.xlsx`,
        );
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      } catch (err) {
        const msg =
          err?.response?.data?.message ||
          err?.message ||
          "Erreur lors de l'exportation.";
        notificationService.toastError(msg);
      } finally {
        this.exporting = false;
      }
    },
  },
  watch: {
    filters: {
      deep: true,
      handler() {
        this.scheduleFetch();
      },
    },
  },
};
</script>

<template>
  <div class="companies-list-container mt-4">
    <div class="list-card">
      <ListHeader title="Entreprises enregistrées">
        <template #actions>
          <button
            type="button"
            class="list-btn list-btn--ghost"
            title="Exporter la liste des entreprises"
            :disabled="exporting"
            @click="exportCompanies"
          >
            <i class="ri-file-excel-line"></i>
            {{ exporting ? 'Export…' : 'Exporter' }}
          </button>
        </template>
      </ListHeader>

      <CompaniesFilters
        v-model="filters"
        v-model:expanded="panelOpen"
        :options="filterOptions"
        :active-count="activeCount"
        :criteria="criteria"
        :applied-filter-id="appliedFilterId"
        @reset="clearAllFilters"
        @apply-saved="applySavedFilter"
        @saved-filters-loaded="onSavedFiltersLoaded"
      />

      <FilterChips
        :chips="chips"
        @clear="clearFilter"
        @clear-all="clearAllFilters"
      />

      <CompaniesTable
        :companies="companies"
        :loading="loading"
        :error="error"
        :has-filters="hasFilters"
        @view="viewCompany"
        @new-survey="startSurveyFor"
        @delete="confirmDelete"
        @clear-filters="clearAllFilters"
      />

      <ListPagination
        :current-page="currentPage"
        :per-page="perPage"
        :total="totalCompanies"
        :total-pages="totalPages"
        noun="entreprises"
        @page-change="changePage"
      />
    </div>

    <DeleteCompanyModal
      v-model="deleteModal"
      :company="companyToDelete"
      @deleted="onDeleted"
    />
  </div>
</template>
